import { buildSelection, quantityRule, addSelection } from './verve-selection.js';

class VerveStory extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    if ('requiresParam' in this.dataset) {
      // Product-page ad-set panel: stay hidden and uninitialised unless the link carries ?set=<key>.
      if (new URLSearchParams(location.search).get('set') !== this.dataset.set) return;
      this.hidden = false;
    }
    this.ready = true;
    this.variants = new Map();
    this.sequence = 0;
    this.pending = false;
    this.uncertain = false;
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency: this.dataset.currency || 'AUD' });
    this.initCards(this);
    this.classList.add('vs-ready');
    const first = [...this.querySelectorAll('article[data-product]')].find(card => !card.classList.contains('vs-catalog-card'));
    if (first) this.openCard(first, true);
    this.addEventListener('change', event => this.change(event));
    this.addEventListener('input', event => { if (event.target.matches('[data-quantity]')) this.update(); });
    this.addEventListener('click', event => this.click(event));
    // Handle an intentional touch tap before Safari's emulated hover/click cycle.
    // A swipe, cancelled gesture or multi-touch must retain native behaviour.
    this.addEventListener('touchstart', event => {
      const link = event.target.closest('[data-scroll]');
      this.scrollTouch = event.touches.length === 1 && link ? { link, id: event.touches[0].identifier, x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    this.addEventListener('touchmove', event => {
      const touch = [...event.touches].find(item => item.identifier === this.scrollTouch?.id);
      if (!touch || event.touches.length !== 1 || Math.hypot(touch.clientX - this.scrollTouch.x, touch.clientY - this.scrollTouch.y) > 10) this.scrollTouch = null;
    }, { passive: true });
    this.addEventListener('touchcancel', () => { this.scrollTouch = null; }, { passive: true });
    this.addEventListener('touchend', event => {
      const gesture = this.scrollTouch;
      this.scrollTouch = null;
      const touch = [...event.changedTouches].find(item => item.identifier === gesture?.id);
      if (!gesture || !touch || event.touches.length || !event.cancelable || Math.hypot(touch.clientX - gesture.x, touch.clientY - gesture.y) > 10) return;
      event.preventDefault(); // Suppress the emulated click: scroll exactly once.
      event.stopPropagation();
      this.navigateToSection(gesture.link);
    }, { passive: false });
    this.querySelector('[data-order-form]').addEventListener('submit', event => { event.preventDefault(); this.submit(event.submitter?.value === 'checkout' ? 'checkout' : 'cart'); });
    // Browser back after a cart/checkout hand-off: the page may return from the back-forward cache with
    // controls still disabled and rows the browser restored. Reset to a clean, re-usable state.
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      this.pending = false; this.uncertain = false;
      this.querySelectorAll('select,input,button').forEach(el => { el.disabled = false; });
      this.querySelectorAll('[data-order-row]').forEach(row => { const q = row.querySelector('[data-quantity]'); q.disabled = !row.querySelector('[data-options]').value; });
      this.querySelectorAll('[data-submit]').forEach(button => { button.textContent = button.value === 'checkout' ? 'Purchase Now' : 'Add To Cart'; });
      this.querySelector('[data-message]').textContent = '';
      this.querySelector('[data-order-form]').setAttribute('aria-busy', 'false');
      this.clearRows(this);
      this.update();
      this.refreshCart();
      this.dispatchEvent(new CustomEvent('vs:restored'));
    });
    this.update();
    this.refreshCart();
    this.prefill();
    this.loadCatalog();
  }
  // ?add=SKU:qty,SKU  (or a numeric variant id) pre-selects rows so a rep or an ad can hand over a ready-made selection.
  // Matches only variants rendered on this page; malformed tokens are ignored and never block the page.
  prefill() {
    try {
      const param = new URLSearchParams(location.search).get('add');
      if (!param) return;
      for (const token of param.split(',')) {
        const [code, qtyText] = token.split(':');
        const key = (code || '').trim();
        if (!key) continue;
        const option = [...this.querySelectorAll('article[data-product]:not([data-host]) [data-options] option[value]')].find(o => o.value && !o.disabled && (o.dataset.sku === key || o.value === key));
        if (!option) continue;
        const card = option.closest('article[data-product]');
        const chip = card.querySelector(`[data-choose="${option.value}"]`);
        if (!chip || chip.disabled) continue;
        this.openCard(card, true);
        chip.click();
        const row = [...card.querySelectorAll('[data-order-row]')].find(r => r.querySelector('[data-options]').value === option.value);
        const quantity = Number.parseInt(qtyText, 10);
        if (row && Number.isSafeInteger(quantity) && quantity > 0) { const input = row.querySelector('[data-quantity]'); input.value = Math.min(quantity, Number(input.max) || 999); }
      }
      this.update();
    } catch { /* never block the page on a bad ?add= */ }
  }
  // Registers option lists, rows and pickers for every card inside `scope`; used at start-up and for cards fetched later.
  initCards(scope) {
    scope.querySelectorAll('[data-options]').forEach(select => {
      const options = [...select.options].filter(option => option.value);
      if (options.every(option => /^\d+(?:\.\d+)?kg\b/i.test(option.dataset.title))) {
        options.sort((a,b) => parseFloat(a.dataset.title) - parseFloat(b.dataset.title)).forEach(option => select.append(option));
      }
    });
    scope.querySelectorAll('[data-product-image]').forEach(img => { img.dataset.initialSrc = img.src; img.dataset.initialAlt = img.alt; });
    scope.querySelectorAll('[data-options] option[value]:not([value=""])').forEach(option => {
      this.variants.set(option.value, { product: option.dataset.product, extra: option.dataset.extra === 'true', title: option.dataset.title, unavailableLabel: option.dataset.unavailableLabel || 'Sold out', price: Number(option.dataset.price), available: !option.disabled, min: Number(option.dataset.min || 1), max: option.dataset.max ? Number(option.dataset.max) : null, increment: Number(option.dataset.increment || 1) });
    });
    scope.querySelectorAll('[data-order-row]').forEach(row => this.labelRow(row));
    scope.querySelectorAll('[data-enhanced]').forEach(el => { el.hidden = false; });
    scope.querySelectorAll('[data-fallback]').forEach(el => { el.hidden = true; });
    scope.querySelectorAll('article[data-product]').forEach(card => { this.makePicker(card); this.openCard(card, false); });
  }
  // Product-page panels fetch the browse-the-range catalog on demand (data-catalog-url) so ordinary product
  // pages carry none of its weight. Products already in the set are dropped; the rest join this selection.
  async loadCatalog() {
    const url = this.dataset.catalogUrl;
    if (!url || this.catalogLoaded) return;
    this.catalogLoaded = true;
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const catalog = doc.querySelector('.vs-catalog');
      const grid = this.querySelector('.vs-grid');
      if (!catalog || !grid) return;
      const inSet = new Set((this.dataset.setHandles || '').split(',').filter(Boolean));
      catalog.querySelectorAll('article[data-handle]').forEach(card => { if (inSet.has(card.dataset.handle)) card.remove(); });
      catalog.querySelectorAll('[data-catalog-group]').forEach(group => { if (!group.querySelector('article')) { group.remove(); catalog.querySelector(`[data-catalog-pill="${group.dataset.catalogGroup}"]`)?.remove(); } });
      grid.append(catalog);
      this.initCards(catalog);
      this.update();
    } catch { this.catalogLoaded = false; }
  }
  makePicker(card) {
    const picker = card.querySelector('[data-picker]');
    const values = [...card.querySelector('[data-options]').options].filter(option => option.value);
    picker.classList.toggle('vs-picker-wide', values.length <= 3);
    for (const option of card.querySelector('[data-options]').options) {
      if (!option.value) continue;
      const variant = this.variants.get(option.value);
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'vs-choice'; button.dataset.choose = option.value;
      button.disabled = !variant.available;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', `${variant.product}, ${variant.title}, ${this.format(variant.price)}${variant.available ? '' : ', unavailable'}`);
      const label = document.createElement('strong');
      label.textContent = variant.title.match(/^\d+(?:\.\d+)?kg\b/i)?.[0] || variant.title;
      const price = document.createElement('span'); price.textContent = this.format(variant.price);
      button.append(label, price);
      if (!variant.available || /pre.?order/i.test(variant.title)) {
        const note = document.createElement('small'); note.textContent = variant.available ? 'Preorder' : variant.unavailableLabel; button.append(note);
      }
      picker.append(button);
    }
  }
  openCard(card, open) {
    card.querySelector('[data-card-toggle]').setAttribute('aria-expanded', String(open));
    card.querySelector('[data-card-body]').hidden = !open;
    card.classList.toggle('is-open', open);
  }
  toggleCard(card) {
    cancelAnimationFrame(this.accordionFrame);
    // Keep the tapped header in place while the previous accordion closes.
    // Otherwise its removed height and browser scroll anchoring compete with our scroll.
    const topBefore = card.getBoundingClientRect().top;
    const open = card.querySelector('[data-card-toggle]').getAttribute('aria-expanded') !== 'true';
    window.scrollTo({ top: window.scrollY, behavior: 'instant' });
    this.querySelectorAll('article[data-product]').forEach(other => {
      if (other.dataset.extra === card.dataset.extra && !other.classList.contains('vs-extra-single')) this.openCard(other, other === card && open);
    });
    const shift = card.getBoundingClientRect().top - topBefore;
    window.scrollTo({ top: window.scrollY + shift, behavior: 'instant' });
    if (!open || !matchMedia('(max-width: 749px)').matches) return;
    this.accordionFrame = requestAnimationFrame(() => {
      const viewport = window.visualViewport;
      let top = viewport?.offsetTop || 0;
      let bottom = top + (viewport?.height || innerHeight);
      document.querySelectorAll('header, sticky-header, [class*="header"], [id*="header"]').forEach(el => {
        if (this.contains(el)) return;
        const rect = el.getBoundingClientRect(), style = getComputedStyle(el);
        if (['fixed', 'sticky'].includes(style.position) && rect.height > 0 && rect.height < innerHeight / 2 && rect.top <= top + 1 && rect.bottom > top) top = Math.max(top, rect.bottom);
      });
      const purchaseBar = this.querySelector('.vs-mobile-selection');
      if (!purchaseBar.hidden) bottom = Math.min(bottom, purchaseBar.getBoundingClientRect().top);
      const picker = card.querySelector('[data-picker]').getBoundingClientRect();
      const destination = window.scrollY + picker.top + picker.height / 2 - (top + bottom) / 2;
      window.scrollTo({ top: Math.max(0, destination), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    });
  }
  scrollToTarget(target) {
    if (!target) return;
    // Measure visible fixed/sticky headers instead of assuming the same height on every theme.
    let clearance = 16;
    document.querySelectorAll('header, sticky-header, [class*="header"], [id*="header"]').forEach(el => {
      if (this.contains(el)) return;
      const style = getComputedStyle(el), rect = el.getBoundingClientRect();
      if (['fixed', 'sticky'].includes(style.position) && rect.height > 0 && rect.height < innerHeight / 2 && rect.top <= 1 && rect.bottom > 0) clearance = Math.max(clearance, rect.bottom + 16);
    });
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Move focus before starting the animation: Safari can interrupt a smooth
    // scroll when focus changes, even when preventScroll is requested.
    target.setAttribute('tabindex', '-1'); target.focus({ preventScroll: true });
    window.scrollTo({ top: Math.max(0, target.getBoundingClientRect().top + window.scrollY - clearance), behavior: reduced ? 'instant' : 'smooth' });
  }
  navigateToSection(link) {
    const target = this.querySelector(link.getAttribute('href'));
    if (target?.classList.contains('vs-shop') && !this.querySelector('article[data-extra="false"].is-open')) this.openCard(this.querySelector('article[data-extra="false"]'), true);
    this.scrollToTarget(target);
  }
  format(cents) { return this.money.format(cents / 100); }
  labelRow(row) {
    const prefix = `${this.id}-row-${++this.sequence}`;
    row.querySelector('[data-options]').id = `${prefix}-option`;
    row.querySelector('[data-option-label]').htmlFor = `${prefix}-option`;
    row.querySelector('[data-quantity]').id = `${prefix}-quantity`;
    row.querySelector('[data-quantity-label]').htmlFor = `${prefix}-quantity`;
  }
  change(event) {
    if (!event.target.matches('[data-options]')) return;
    const select = event.target;
    const option = select.selectedOptions[0];
    const row = select.closest('[data-order-row]');
    const quantity = row.querySelector('[data-quantity]');
    const variant = this.variants.get(select.value);
    quantity.disabled = !variant;
    if (variant) {
      const rule = quantityRule(variant);
      quantity.min = rule.min; quantity.max = rule.max; quantity.step = rule.increment; quantity.value = rule.min;
    }
    row.querySelector('[data-chosen]').textContent = variant ? `${variant.title} · ${this.format(variant.price)} each` : '';
    if (variant) {
      quantity.setAttribute('aria-label', `${variant.product} ${variant.title} quantity`);
      row.querySelector('[data-remove-option]').setAttribute('aria-label', `Remove ${variant.product} ${variant.title}`);
      row.querySelector('[data-step="-1"]').setAttribute('aria-label', `Decrease ${variant.product} ${variant.title} quantity`);
      row.querySelector('[data-step="1"]').setAttribute('aria-label', `Increase ${variant.product} ${variant.title} quantity`);
    }
    const card = select.closest('[data-product]');
    const photo = card.querySelector('[data-product-image]');
    if (option?.dataset.image) { photo.src = option.dataset.image; photo.removeAttribute('srcset'); photo.alt = `${variant.product} — ${variant.title}`; }
    else { photo.src = photo.dataset.initialSrc; photo.alt = photo.dataset.initialAlt; }
    card.querySelector('[data-detail]').href = select.value ? `${card.dataset.url}${card.dataset.url.includes('?') ? '&' : '?'}variant=${encodeURIComponent(select.value)}` : card.dataset.url;
    this.update();
  }
  click(event) {
    const scroll = event.target.closest('[data-scroll]');
    if (scroll) {
      event.preventDefault(); event.stopPropagation();
      this.navigateToSection(scroll); return;
    }
    const pill = event.target.closest('[data-catalog-pill]');
    if (pill) {
      const open = pill.getAttribute('aria-expanded') !== 'true';
      this.querySelectorAll('[data-catalog-pill]').forEach(p => p.setAttribute('aria-expanded', String(p === pill && open)));
      this.querySelectorAll('[data-catalog-group]').forEach(g => { g.hidden = !(open && g.dataset.catalogGroup === pill.dataset.catalogPill); });
      if (open) { const group = this.querySelector(`[data-catalog-group="${pill.dataset.catalogPill}"]`); const first = group && group.querySelector('article[data-product]'); if (first && !group.querySelector('article.is-open')) this.openCard(first, true); }
      return;
    }
    const toggle = event.target.closest('[data-card-toggle]');
    if (toggle) {
      event.preventDefault(); event.stopPropagation();
      this.toggleCard(toggle.closest('article[data-product]'));
      return;
    }
    const remove = event.target.closest('[data-remove-option]');
    if (this.pending || this.uncertain) return;
    if (event.target.closest('[data-buy]')) { this.submit('checkout'); return; }
    const choice = event.target.closest('[data-choose]');
    if (choice) {
      const card = choice.closest('article[data-product]');
      const rows = [...card.querySelectorAll('[data-order-row]')];
      const existing = rows.find(row => row.querySelector('[data-options]').value === choice.dataset.choose);
      if (existing) { existing.querySelector('[data-quantity]').focus({preventScroll:true}); return; }
      let row = rows.find(row => !row.querySelector('[data-options]').value);
      if (!row) {
        if (rows.length >= 15) { this.querySelector('[data-message]').textContent = 'Choose up to 15 options per product.'; return; }
        row = rows[0].cloneNode(true); this.labelRow(row); card.querySelector('[data-rows]').append(row);
      }
      const select = row.querySelector('[data-options]'); select.value = choice.dataset.choose;
      this.change({target:select}); return;
    }
    const step = event.target.closest('[data-step]');
    if (step) {
      const row = step.closest('[data-order-row]');
      const input = row.querySelector('[data-quantity]');
      const rule = quantityRule(this.variants.get(row.querySelector('[data-options]').value));
      const current = Number(input.value);
      input.value = Math.min(rule.max, Math.max(rule.min, (Number.isSafeInteger(current) ? current : rule.min) + Number(step.dataset.step) * rule.increment));
      this.update(); return;
    }
    if (remove) {
      const card = remove.closest('[data-product]');
      const row = remove.closest('[data-order-row]');
      if (card.querySelectorAll('[data-order-row]').length > 1) {
        row.remove();
      } else {
        row.querySelector('[data-options]').value = '';
        row.querySelector('[data-quantity]').value = 1;
        row.querySelector('[data-quantity]').disabled = true;
        row.querySelector('[data-chosen]').textContent = '';
      }
      const lastChosen = [...card.querySelectorAll('[data-options]')].filter(select => select.value).at(-1);
      const photo = card.querySelector('[data-product-image]');
      photo.src = lastChosen?.selectedOptions[0]?.dataset.image || photo.dataset.initialSrc;
      photo.alt = lastChosen ? `${this.variants.get(lastChosen.value).product} — ${this.variants.get(lastChosen.value).title}` : photo.dataset.initialAlt;
      card.querySelector('[data-detail]').href = lastChosen ? `${card.dataset.url}${card.dataset.url.includes('?') ? '&' : '?'}variant=${lastChosen.value}` : card.dataset.url;
      card.querySelector('[data-choose]:not(:disabled)')?.focus({preventScroll:true});
    }
    if (remove) this.update();
  }
  // Lists set products already in the cart (matched by product handle from data-set-handles) and keeps
  // cartTotal current so Purchase now stays available for them. No-op when the markup has no cart block.
  async refreshCart(attempt = 0) {
    const block = this.querySelector('[data-cart-block]');
    if (!block || !this.dataset.cartUrl) return;
    const handles = (this.dataset.setHandles || '').split(',').filter(Boolean);
    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`cart ${response.status}`);
      const cart = await response.json();
      const lines = (cart.items || []).filter(item => handles.includes(item.handle));
      const list = block.querySelector('[data-cart-list]');
      list.replaceChildren();
      let total = 0;
      for (const item of lines) {
        const li = document.createElement('li');
        const name = document.createElement('strong'); name.textContent = item.product_title;
        const detail = document.createElement('span'); detail.textContent = `${item.variant_title ? item.variant_title + ' ' : ''}× ${item.quantity}`;
        const amount = document.createElement('span'); amount.textContent = this.format(item.final_line_price);
        li.append(name, detail, amount); list.append(li);
        total += item.final_line_price;
      }
      block.querySelector('[data-cart-total]').textContent = this.format(total);
      block.hidden = lines.length === 0;
      this.cartTotal = total;
      this.update();
    } catch {
      // A read straight after a back-navigation or checkout hand-off can fail once; try again shortly.
      if (attempt < 1) setTimeout(() => this.refreshCart(attempt + 1), 600);
    }
  }
  clearRows(scope = this) {
    scope.querySelectorAll('[data-order-row]').forEach(row => { row.querySelector('[data-options]').value = ''; row.querySelector('[data-quantity]').value = 1; row.querySelector('[data-quantity]').disabled = true; row.querySelector('[data-chosen]').textContent = ''; });
  }
  readSelection(scope = this) {
    return buildSelection([...scope.querySelectorAll('[data-order-row]')].map(row => ({ id: row.querySelector('[data-options]').value, quantity: row.querySelector('[data-quantity]').value })), this.variants);
  }
  update() {
    if (this.pending || this.uncertain) return;
    const message = this.querySelector('[data-message]');
    const buttons = this.querySelectorAll('[data-submit],[data-buy]');
    let selection;
    try { selection = this.readSelection(); message.textContent = ''; }
    catch (error) { message.textContent = error.message; buttons.forEach(button => { button.disabled = true; }); this.querySelector('[data-total]').textContent = 'Check quantity'; this.querySelector('[data-mobile-total]').textContent = 'Check quantity'; return; }
    // cartTotal (cents) is set by a host page when set products are already in the cart. Purchase now
    // always checks out the whole cart, so it stays live for those; Add to cart only adds new rows.
    const cartTotal = Number(this.cartTotal) || 0;
    buttons.forEach(button => { button.disabled = button.matches('[data-buy],[value="checkout"]') ? !(selection.items.length || cartTotal) : !selection.items.length; });
    this.querySelector('[data-total]').textContent = this.format(selection.total);
    const totalLabel = this.querySelector('[data-total-label]'); if (totalLabel) totalLabel.textContent = cartTotal ? 'Adding now' : 'Subtotal';
    const checkoutRow = this.querySelector('[data-checkout-row]'); if (checkoutRow) { checkoutRow.hidden = !cartTotal; checkoutRow.querySelector('[data-checkout-total]').textContent = this.format(selection.total + cartTotal); }
    this.querySelector('[data-count]').textContent = `${selection.count} ${selection.count === 1 ? 'unit' : 'units'} selected`;
    this.querySelectorAll('[data-selection-count]').forEach(el => { el.textContent = String(selection.count); });
    this.querySelector('[data-mobile-label]').textContent = selection.count ? `${selection.count} ${selection.count === 1 ? 'unit' : 'units'} · Review` : cartTotal ? 'Your cart · Review' : 'Choose your first piece';
    this.querySelector('[data-mobile-total]').textContent = selection.count || cartTotal ? this.format(selection.total + cartTotal) : '';
    this.querySelector('.vs-mobile-selection').hidden = !(selection.count || cartTotal);
    this.classList.toggle('vs-summary-empty', !(selection.count || cartTotal));
    this.querySelector('[data-mobile-review]').href = `#${this.querySelector(selection.count ? '.vs-summary' : '.vs-shop').id}`;
    const list = this.querySelector('[data-summary-list]');
    list.replaceChildren();
    if (!selection.items.length) {
      const empty = document.createElement('li'); empty.className = 'vs-empty'; empty.textContent = cartTotal ? 'Add more from the set above, or purchase what is already in your cart.' : 'Pick your first piece above. Your lineup will appear here.'; list.append(empty);
    }
    for (const item of selection.items) {
      const variant = this.variants.get(String(item.id));
      const li = document.createElement('li');
      const name = document.createElement('strong'); name.textContent = variant.product;
      const detail = document.createElement('span'); detail.textContent = `${variant.title} × ${item.quantity}`;
      const amount = document.createElement('span'); amount.textContent = this.format(variant.price * item.quantity);
      li.append(name, detail, amount); list.append(li);
      if (variant.extra) { const label = document.createElement('small'); label.className = 'vs-extra-label'; label.textContent = 'Optional extra'; li.append(label); }
    }
    this.querySelectorAll('article[data-product]').forEach(card => {
      const chosen = new Set(); let count = 0;
      card.querySelectorAll('[data-order-row]').forEach(row => {
        const id = row.querySelector('[data-options]').value;
        row.hidden = !id;
        if (id) {
          chosen.add(id); const input = row.querySelector('[data-quantity]'); count += Number(input.value);
          row.querySelector('[data-step="-1"]').disabled = Number(input.value) <= Number(input.min);
          row.querySelector('[data-step="1"]').disabled = Number(input.value) >= Number(input.max);
        }
      });
      card.querySelectorAll('[data-choose]').forEach(button => button.setAttribute('aria-pressed', String(chosen.has(button.dataset.choose))));
      card.querySelector('[data-card-status]').textContent = count ? `${count} ${count === 1 ? 'unit' : 'units'} selected` : card.dataset.extra === 'true' ? 'Optional extra' : 'Choose your options';
      card.classList.toggle('has-selection', count > 0);
      const quickExtra = card.querySelector('[data-quick-extra]');
      if (quickExtra) { quickExtra.hidden = count > 0; this.openCard(card, count > 0); }
    });
  }
  // destination 'silent' adds the rows inside `scope` (default: all rows) and returns without leaving the page.
  // Used by a host product form's own Add to cart so partner products travel with it.
  async submit(destination = 'cart', scope = this) {
    if (this.pending || this.uncertain) return;
    let selection;
    try { selection = this.readSelection(scope); } catch { this.update(); return; }
    if (!selection.items.length) {
      if (destination === 'checkout' && Number(this.cartTotal) > 0) {
        // Nothing new to add: go straight to checkout with the cart as it stands.
        const form = this.querySelector('[data-checkout-form]');
        form.querySelector('[name="checkout"]').disabled = false;
        HTMLFormElement.prototype.submit.call(form);
      }
      return;
    }
    this.pending = true;
    const message = this.querySelector('[data-message]');
    const button = this.querySelector(`[data-submit][value="${destination}"]`);
    const controls = [...this.querySelectorAll('select,input,button')].filter(el => !el.disabled);
    this.querySelector('[data-order-form]').setAttribute('aria-busy', 'true');
    controls.forEach(el => { el.disabled = true; });
    if (button) button.textContent = 'Adding your selection…';
    message.textContent = 'Please wait while we add your selection.';
    try {
      // Cart-level attribute records which ad set the visitor shopped from, for order reporting.
      // Not a line property: this theme prints line properties in the cart drawer. Written and awaited
      // before the add so a host page's own add-to-cart request cannot race ahead of it.
      if (this.dataset.set) await fetch(this.dataset.cartAdd.replace(/add\.js$/, 'update.js'), { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { ad_set: this.dataset.set } }) }).catch(() => {});
      await addSelection(selection.items, this.dataset.cartAdd);
      if (destination === 'silent') {
        // Clear the rows that were just added so a second Add to cart cannot duplicate them.
        this.clearRows(scope);
        controls.forEach(el => { el.disabled = false; });
        this.pending = false;
        message.textContent = 'Added to your cart.';
        this.update();
        return;
      }
      message.textContent = destination === 'checkout' ? 'Selection added. Opening checkout…' : 'Selection added. Opening your cart…';
      // Clear before leaving so browser form restoration on Back cannot resurrect rows that are already in the cart.
      this.clearRows(scope);
      if (destination === 'checkout') {
        // Native Shopify checkout submission preserves the existing cart and locale.
        const form = this.querySelector('[data-checkout-form]');
        form.querySelector('[name="checkout"]').disabled = false;
        HTMLFormElement.prototype.submit.call(form);
      } else window.location.assign(this.dataset.cartUrl);
    } catch {
      this.uncertain = true;
      if (button) button.textContent = 'Review your cart';
      message.textContent = 'We couldn’t confirm the full selection. Some items may have been added. Review your cart before trying again.';
      const link = this.querySelector('[data-review-cart]'); link.hidden = false; link.focus();
      if (destination === 'silent') throw new Error('Selection not confirmed');
    } finally {
      this.pending = false;
      this.querySelector('[data-order-form]').setAttribute('aria-busy', 'false');
    }
  }
}
if (!customElements.get('verve-story')) customElements.define('verve-story', VerveStory);
